import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAvailableProperties, useImportContacts, usePreviewContactImport } from '@/hooks/useApi'
import type { ContactImportPreview, ContactImportResult } from '@/types/api'

// Mirrors the backend contract: "ignore" is the sentinel for a column left out.
const IGNORED_TARGET = 'ignore'
const CORE_FIELDS = ['email', 'first_name', 'last_name', 'company'] as const

export function ContactImportDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('contacts')
  const previewImport = usePreviewContactImport()
  const importContacts = useImportContacts()
  const { data: availableProperties } = useAvailableProperties()

  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ContactImportPreview | null>(null)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [result, setResult] = useState<ContactImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setFile(null)
      setPreview(null)
      setMapping({})
      setResult(null)
      setError(null)
    }
  }, [open])

  // Custom HubSpot-style properties can receive a column too, so an import is not
  // limited to the four built-in contact fields.
  const customTargets = useMemo(
    () => (availableProperties ?? []).filter((property) => !property.builtin),
    [availableProperties],
  )

  const hasEmailColumn = Object.values(mapping).includes('email')

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0] ?? null
    setPreview(null)
    setMapping({})
    setResult(null)
    setError(null)
    setFile(chosen)
    if (!chosen) return

    previewImport.mutate(chosen, {
      onSuccess: (data) => {
        setPreview(data)
        setMapping(data.suggested_mapping)
      },
      onError: (previewError: Error) => setError(previewError.message),
    })
  }

  const handleImport = () => {
    if (!file || !hasEmailColumn) return
    setError(null)
    importContacts.mutate(
      { file, mapping },
      {
        onSuccess: setResult,
        onError: (importError: Error) => setError(importError.message),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('importDialog.title')}</DialogTitle>
          <DialogDescription>{t('importDialog.description')}</DialogDescription>
        </DialogHeader>

        {result ? (
          <ImportSummary result={result} />
        ) : (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label htmlFor="contact-csv">{t('importDialog.chooseFile')}</Label>
              <Input id="contact-csv" type="file" accept=".csv" onChange={handleFileChange} />
              <p className="text-xs text-muted-foreground">{t('importDialog.fileHint')}</p>
            </div>

            {previewImport.isPending && (
              <p className="flex items-center text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('importDialog.analyzing')}
              </p>
            )}

            {preview && (
              <>
                <div className="space-y-2">
                  <h3 className="text-sm font-medium">{t('importDialog.mappingTitle')}</h3>
                  <p className="text-xs text-muted-foreground">
                    {t('importDialog.rowsFound', { count: preview.total_rows })}
                  </p>
                  <div className="space-y-2">
                    {preview.columns.map((column) => (
                      <div key={column} className="flex items-center gap-3">
                        <span className="w-1/2 truncate text-sm">{column}</span>
                        <Select
                          value={mapping[column] ?? IGNORED_TARGET}
                          onValueChange={(value) =>
                            setMapping((current) => ({ ...current, [column]: value }))
                          }
                        >
                          <SelectTrigger className="w-1/2" aria-label={column}>
                            <SelectValue placeholder={t('importDialog.field')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={IGNORED_TARGET}>
                              {t('importDialog.fields.ignore')}
                            </SelectItem>
                            {CORE_FIELDS.map((field) => (
                              <SelectItem key={field} value={field}>
                                {t(`importDialog.fields.${field}`)}
                              </SelectItem>
                            ))}
                            {customTargets.map((property) => (
                              <SelectItem key={property.key} value={property.key}>
                                {property.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                  {!hasEmailColumn && (
                    <p role="alert" className="text-sm text-destructive">
                      {t('importDialog.emailRequired')}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-medium">{t('importDialog.previewTitle')}</h3>
                  <div className="overflow-x-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {preview.columns.map((column) => (
                            <TableHead key={column}>{column}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.rows.map((row, index) => (
                          <TableRow key={index}>
                            {preview.columns.map((column) => (
                              <TableCell key={column}>{row[column]}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            )}

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => onOpenChange(false)}>{t('importDialog.result.close')}</Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('importDialog.cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleImport}
                disabled={!preview || !hasEmailColumn || importContacts.isPending}
              >
                {importContacts.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {importContacts.isPending ? t('importDialog.submitting') : t('importDialog.submit')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ImportSummary({ result }: { result: ContactImportResult }) {
  const { t } = useTranslation('contacts')
  return (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto">
      <h3 className="text-sm font-medium">{t('importDialog.result.title')}</h3>
      <ul className="space-y-1 text-sm">
        <li>{t('importDialog.result.created', { count: result.created })}</li>
        <li>{t('importDialog.result.duplicates', { count: result.skipped_duplicates })}</li>
        <li>{t('importDialog.result.invalid', { count: result.skipped_invalid })}</li>
      </ul>

      {result.errors.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-sm font-medium">{t('importDialog.result.errorsTitle')}</h4>
          <ul className="space-y-1 text-sm text-muted-foreground">
            {result.errors.map((rowError) => (
              <li key={rowError.row}>
                {t('importDialog.result.row', { row: rowError.row })}
                {rowError.email ? ` — ${rowError.email}` : ''} —{' '}
                {t(`importDialog.reasons.${rowError.reason}`, { defaultValue: rowError.reason })}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
